import type { OracleToken } from "./types.ts";

// Un run = secuencia máxima de tokens consecutivos del mismo speaker.
// Se corta cada vez que aparece un token de otro speaker.
export type SpeakerRun = {
  speakerIndex: number;
  startTime: number;
  endTime: number;
  duration: number;
  tokens: OracleToken[];
  avgConfidence: number;
  score: number;
};

export type PickSamplesOptions = {
  /** No considerar runs antes de este segundo (default: 60). */
  minStartTime?: number;
  /** Duración mínima del run en segundos (default: 2). */
  minDuration?: number;
  /** Separación mínima entre samples elegidos, en segundos (default: 30). */
  minSeparation?: number;
  /** Cuántos samples devolver como máximo (default: 3). */
  count?: number;
};

/**
 * Agrupa tokens en runs por speaker. Asume tokens en orden cronológico.
 */
export function findSpeakerRuns(tokens: OracleToken[]): SpeakerRun[] {
  if (tokens.length === 0) return [];

  const runs: SpeakerRun[] = [];
  let buffer: OracleToken[] = [tokens[0]];

  const flush = () => {
    const startTime = buffer[0].startTime;
    const endTime = buffer.at(-1)!.endTime;
    const duration = endTime - startTime;
    const avgConfidence =
      buffer.reduce((acc, t) => acc + t.confidence, 0) / buffer.length;
    // Score premia: duración, confianza, y densidad de tokens.
    // log(n+1) evita que runs de pocas palabras muy largos dominen.
    const score = duration * avgConfidence * Math.log(buffer.length + 1);
    runs.push({
      speakerIndex: buffer[0].speakerIndex,
      startTime,
      endTime,
      duration,
      tokens: [...buffer],
      avgConfidence,
      score,
    });
  };

  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.speakerIndex === buffer[0].speakerIndex) {
      buffer.push(t);
    } else {
      flush();
      buffer = [t];
    }
  }
  flush();
  return runs;
}

/**
 * Selecciona hasta `count` samples para un speaker:
 *  1. Filtra por minStartTime y minDuration.
 *  2. Ordena por score descendente.
 *  3. Greedy: agrega el siguiente mejor si está a ≥minSeparation de todos
 *     los ya elegidos. Así evitamos 3 samples consecutivos del mismo bloque.
 *  4. Devuelve los elegidos en orden cronológico.
 */
export function pickTopSamples(
  runs: SpeakerRun[],
  speakerIndex: number,
  opts: PickSamplesOptions = {},
): SpeakerRun[] {
  const {
    minStartTime = 60,
    minDuration = 2,
    minSeparation = 30,
    count = 3,
  } = opts;

  const candidates = runs
    .filter(
      (r) =>
        r.speakerIndex === speakerIndex &&
        r.duration >= minDuration &&
        r.startTime >= minStartTime,
    )
    .sort((a, b) => b.score - a.score);

  const picked: SpeakerRun[] = [];
  for (const c of candidates) {
    if (picked.length >= count) break;
    const tooClose = picked.some(
      (p) => Math.abs(c.startTime - p.startTime) < minSeparation,
    );
    if (!tooClose) picked.push(c);
  }

  return picked.sort((a, b) => a.startTime - b.startTime);
}
